import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnInit} from '@angular/core';
import { environment } from '@env/environment';
import { APPLICATION_STATUS, FIRM_APPLICATION_TYPES, FIRM_CERTIFICATE_ATTENTION_FILTERS, LEAD_BASED_PAINT } from '@shared/utils/app-static-data';
import { AuthService } from 'app/auth/auth.service';
import { SharedService } from 'app/services/core/shared.service';
import { FeatureFlagsService } from 'app/services/core/feature-flags.service';
import { AtpService } from 'app/services/atp/atp.service';
import { CommonService } from 'app/services/common/common.service';
import { ComplaintDialogService } from 'app/services/complaints/complaint-dialog.service';
import { AtpEditComponent } from '../atp/atp-edit/atp-edit.component';
import { MatDialog } from '@angular/material/dialog';
import { AtpApplicationComponent } from '../atp/atp-application/atp-application.component';
import { IAtpDashboard } from 'app/models/atp/atp-dashboard';
import { Router } from '@angular/router';
import { IFirm } from 'app/models/firms/firm';
import { IFirmApplication } from 'app/models/firms/firm-application';
import { IFirmDashboardData } from 'app/models/firms/firm-dashboard';
import { FirmService } from 'app/services/firms/firm.service';
import { FirmApplicationComponent } from '../firms/firm-application/firm-application.component';
import { FirmEditComponent } from '../firms/firm-edit/firm-edit.component';
import { IIndividual } from 'app/models/individuals/individual';
import { IIndividualApplication } from 'app/models/individuals/individual-application';
import { IIndividualDashboardData } from 'app/models/individuals/individual-dashboard';
import { IndividualService } from 'app/services/individuals/individual.service';
import { IndividualApplicationComponent } from '../individuals/individual-application/individual-application.component';
import { IndividualEditComponent } from '../individuals/individual-edit/individual-edit.component';

type AtpDashboardSummary = {
  affiliatedAtpCount: number;
  expiringAccreditationCount: number;
  expiredAccreditationCount: number;
  openApplicationCount: number;
  submittedApplicationCount: number;
  actionNeededApplicationCount: number;
};

type DashboardEntityKey = 'atp' | 'firm' | 'individual' | 'complaint';
type DashboardSummaryCardKey = 'affiliated' | 'attention' | 'applications';

@Component({
selector: 'app-dashboard',
templateUrl: './dashboard.component.html',
styleUrls: ['./dashboard.component.scss'],
styles: [],
providers: [],
})
export class DashboardComponent implements OnInit {
    private readonly defaultAtpApplicationStatuses = [
      APPLICATION_STATUS.inProgress,
      APPLICATION_STATUS.submitted,
      APPLICATION_STATUS.inReview,
      APPLICATION_STATUS.actionNeeded,
    ];

    private readonly defaultFirmApplicationStatuses = [
      APPLICATION_STATUS.inProgress,
      APPLICATION_STATUS.submitted,
      APPLICATION_STATUS.inReview,
      APPLICATION_STATUS.actionNeeded,
    ];

    private readonly defaultSummaryCardOrder: DashboardSummaryCardKey[] = [
      'affiliated',
      'attention',
      'applications',
    ];

    public environment = environment;        
    public uiData: any = LEAD_BASED_PAINT;

    public atpLoading: boolean = true;
    public atpList:IAtpDashboard[] = [];
  public atpDashboardProviders: IAtpDashboard[] = [];
  public atpDashboardApplications: IAtpDashboard[] = [];
  public atpDashboardSummary: AtpDashboardSummary = this.createEmptyAtpDashboardSummary();
    public firmLoading: boolean = false;
    public firmDashboard: IFirmDashboardData | null = null;
    public individualLoading: boolean = false;
    public individualDashboard: IIndividualDashboardData | null = null;
    public atpPanelExpanded: boolean = false;
    public firmPanelExpanded: boolean = false;
    public individualPanelExpanded: boolean = false;
    public isMobile: boolean = false;

    public applicationTypes = [
      { value: null, label: '--Select--' },
      { value: 'firstaccreditation', label: 'First Accreditation' },
      { value: 'addaccreditation', label: 'New Accreditation' },
      { value: 'reaccreditation', label: 'Reaccreditation' },
      { value: 'ammendment', label: 'Ammendment' }
    ];

    public programTypes = [
      { value: 'rrp', label: 'RRP' },
      { value: 'lbpa', label: 'LBPA' }
    ];    

    public readonly dashboardCarouselConfig = {
      autoHeight: false,
      grabCursor: true,
      navigation: true,
      observeParents: true,
      observer: true,
      slidesPerView: 1,
      spaceBetween: 8,
      watchOverflow: true,
      breakpoints: {
        480: {
          slidesPerView: 1.08,
          spaceBetween: 10,
        },
        600: {
          slidesPerView: 1.22,
          spaceBetween: 12,
        },
        960: {
          slidesPerView: 1.7,
          spaceBetween: 12,
        },
        1400: {
          slidesPerView: 2.1,
          spaceBetween: 12,
        },
      },
    };

    constructor(
    public sharedService: SharedService,
    public authService: AuthService,
    public featureFlags: FeatureFlagsService,
    public dialog: MatDialog,
    private router: Router,
    private atpService: AtpService,
    private commonService: CommonService,
    private complaintDialogService: ComplaintDialogService,
    private firmService: FirmService,
    private individualService: IndividualService,
    private readonly breakpointObserver: BreakpointObserver,
    ) {}

    ngOnInit(): void {        
        this.breakpointObserver.observe([
          Breakpoints.Handset,
        ]).subscribe((result) => {
          this.isMobile = result.matches;
        });

        // Load user profile first, then load dashboard
        this.loadUserProfileAndDashboard();
    }

    statusColor(status: string): string {
      return this.sharedService.getStatusColor(status);
    }

    getApplicationTypeLabel(applicationType: string): string {
      if (!applicationType) return '';
      const option = this.applicationTypes.find(at => at.value === applicationType);
      return option ? option.label : applicationType;
    }

    getProgramTypeLabel(programType: string): string {
      if (!programType) return '';
      const option = this.programTypes.find(pt => pt.value === programType);
      return option ? option.label : programType;
    }

    get hoverHighlightColor(): string {
      return this.sharedService.darkMode ? '#37474f' : '#e0f7fa'; // dark blue-grey for dark mode, light cyan for light mode
    }

    get showFullAtpDashboard(): boolean {
      return this.atpDashboardSummary.affiliatedAtpCount > 0
        || this.atpDashboardSummary.openApplicationCount > 0;
    }

    get showFullFirmDashboard(): boolean {
      return (this.firmDashboard?.summary?.affiliatedFirmCount || 0) > 0
        || (this.firmDashboard?.summary?.openApplicationCount || 0) > 0;
    }

    get atpSummaryCardOrder(): DashboardSummaryCardKey[] {
      const attentionCount = (this.atpDashboardSummary.expiringAccreditationCount || 0)
        + (this.atpDashboardSummary.expiredAccreditationCount || 0);

      return this.getSummaryCardOrder(
        attentionCount > 0,
        (this.atpDashboardSummary.actionNeededApplicationCount || 0) > 0,
      );
    }

    get firmSummaryCardOrder(): DashboardSummaryCardKey[] {
      const attentionCount = (this.firmDashboard?.summary?.expiringCertificateCount || 0)
        + (this.firmDashboard?.summary?.expiredCertificateCount || 0);

      return this.getSummaryCardOrder(
        attentionCount > 0,
        (this.firmDashboard?.summary?.actionNeededApplicationCount || 0) > 0,
      );
    }

    getEntityPanelOrder(entity: DashboardEntityKey): number {
      if (entity === 'complaint') {
        return 100;
      }

      const baseOrder = {
        atp: 0,
        firm: 1,
        individual: 2,
      }[entity];

      return this.hasDashboardEntityContent(entity) ? baseOrder : baseOrder + 10;
    }

    getEntityBorderColor(color: string): string {
      const hexColorMatch = /^#([0-9a-f]{6})$/i.exec(color);

      if (!hexColorMatch) {
        return color;
      }

      const darkenAmount = this.sharedService.darkMode ? -28 : 28;

      const darkenChannel = (startIndex: number): string => {
        const channelValue = parseInt(hexColorMatch[1].substring(startIndex, startIndex + 2), 16);
        const adjustedValue = Math.min(255, Math.max(0, channelValue - darkenAmount));
        return adjustedValue.toString(16).padStart(2, '0');
      };

      return '#' + darkenChannel(0) + darkenChannel(2) + darkenChannel(4);
    }

    loadUserProfileAndDashboard() {
      // Call the getUserProvife method directly to get the user profile
      this.authService.loading = true;
      this.authService.getUserProvife().subscribe({
          next: (userProfile) => {
              this.authService.userProfile = userProfile;
              this.authService.loading = false;                
              
              // Now load the dashboard with the user email
              if (userProfile && userProfile.email) {
                  this.refreshAtpDashboard();
                  if (this.featureFlags.enableFirms) {
                    this.refreshFirmDashboard();
                  } else {
                    this.firmLoading = false;
                    this.firmDashboard = null;
                  }

                  if (this.featureFlags.enableIndividuals) {
                    this.refreshIndividualDashboard();
                  } else {
                    this.individualLoading = false;
                    this.individualDashboard = null;
                  }

                  this.applyDefaultEntityPanelState();
              } else {
                  console.error('User profile loaded but no email found');
                  this.atpLoading = false;
                  this.firmLoading = false;
                  this.individualLoading = false;
                  this.applyDefaultEntityPanelState();
              }
          },
          error: (error) => {
              console.error('Error loading user profile:', error);
              this.authService.loading = false;
              this.atpLoading = false;
              this.firmLoading = false;
                this.individualLoading = false;
              this.applyDefaultEntityPanelState();
          }
      });
    }

    refreshAtpDashboard() {              
      this.resetAtpDashboardState();
      this.atpService.getAtpPublicDashboardData().subscribe({
            next: (result) => {
              // Filter out applications that are Cancelled or Approved
              this.atpList = result.filter(item => {
                // Keep all ATP tiles
                if (item.tileType === 'atp') {
                  return true;
                }
                // For applications, exclude Cancelled and Approved statuses
                if (item.tileType === 'application') {
                  return item.applicationStatus !== 'Cancelled' && item.applicationStatus !== 'Approved';
                }
                return true;
              });
              this.buildAtpDashboardState();
              this.atpLoading = false;
              this.applyDefaultEntityPanelState();
            },
            error: (error) => {
              console.error('Error loading ATP dashboard data:', error);
              this.resetAtpDashboardState();
              this.atpLoading = false;
              this.applyDefaultEntityPanelState();
            }
        });
    }

    refreshFirmDashboard() {
      this.firmLoading = true;
      this.firmService.getFirmDashboardData().subscribe({
        next: (result) => {
          this.firmDashboard = result;
          this.firmLoading = false;
          this.applyDefaultEntityPanelState();
        },
        error: (error) => {
          console.error('Error loading Firm dashboard data:', error);
          this.firmDashboard = {
            summary: {
              affiliatedFirmCount: 0,
              activeFirmCount: 0,
              applicationCount: 0,
              expiringCertificateCount: 0,
              expiredCertificateCount: 0,
              openApplicationCount: 0,
              submittedApplicationCount: 0,
              actionNeededApplicationCount: 0,
            },
            firms: [],
            applications: [],
          };
          this.firmLoading = false;
          this.applyDefaultEntityPanelState();
        }
      });
    }

    refreshIndividualDashboard() {
      this.individualLoading = true;
      this.individualService.getIndividualDashboardData().subscribe({
        next: (result) => {
          this.individualDashboard = result;
          this.individualLoading = false;
          this.applyDefaultEntityPanelState();
        },
        error: (error) => {
          console.error('Error loading Individual dashboard data:', error);
          this.individualDashboard = {
            summary: {
              affiliatedIndividualCount: 0,
              activeIndividualCount: 0,
              applicationCount: 0,
              expiringCertificateCount: 0,
              expiredCertificateCount: 0,
              openApplicationCount: 0,
              submittedApplicationCount: 0,
              actionNeededApplicationCount: 0,
            },
            individuals: [],
            applications: [],
          };
          this.individualLoading = false;
          this.applyDefaultEntityPanelState();
        }
      });
    }

    tileClicked(atp: IAtpDashboard){
      if (atp.tileType === 'atp') {
        this.manageATP(atp);
      } else if (atp.tileType === 'application') {
        this.editApplication(atp.id);
      }
    }
    
    manageATP(atp: IAtpDashboard) {
    const dialogRef = this.dialog.open(AtpEditComponent, {
      width: '95%',
      data: {atpId: atp?.id ?? 0},});
    dialogRef.afterClosed().subscribe(result => {  
      this.refreshAtpDashboard();
    });
  }

  editApplication(atpApplicationId: number) {    
    this.openAtpApplicationDialog(atpApplicationId ? { atpApplicationId } : {});
  }

  openAtpManagement(): void {
    this.seedAtpManagementFilters();
    this.router.navigate(['/atp/management']);
  }

  openExpiringAtps(event: Event): void {
    event.stopPropagation();
    this.seedAtpManagementFilters('Active', null, this.addMonthsToDate(new Date(), 6));
    this.router.navigate(['/atp/management']);
  }

  openExpiredAtps(event: Event): void {
    event.stopPropagation();
    this.seedAtpManagementFilters('Overdue');
    this.router.navigate(['/atp/management']);
  }

  openAtpApplications(): void {
    this.seedAtpApplicationFilters(this.defaultAtpApplicationStatuses);
    this.router.navigate(['/atp/applications']);
  }

  openSubmittedAndInReviewAtpApplications(event: Event): void {
    event.stopPropagation();
    this.seedAtpApplicationFilters([APPLICATION_STATUS.submitted, APPLICATION_STATUS.inReview]);
    this.router.navigate(['/atp/applications']);
  }

  openActionNeededAtpApplications(event: Event): void {
    event.stopPropagation();
    this.seedAtpApplicationFilters([APPLICATION_STATUS.actionNeeded]);
    this.router.navigate(['/atp/applications']);
  }

  openNewAtpApplication(): void {
    this.openAtpApplicationDialog({ initialSubmissionType: 'new' });
  }

  openLinkAtpApplication(): void {
    this.openAtpApplicationDialog({ initialSubmissionType: 'link' });
  }

  getAtpDashboardApplicationTypeLabel(application: IAtpDashboard): string {
    if (application.submissionType === 'link' || application.applicationType === 'link') {
      return 'Account Link';
    }

    return this.getApplicationTypeLabel(application.applicationType);
  }

  getAtpApplicationActionLabel(application: IAtpDashboard): string {
    return application.applicationStatus === APPLICATION_STATUS.inProgress || application.applicationStatus === APPLICATION_STATUS.actionNeeded
      ? 'Click to continue application'
      : 'Click to view details';
  }

  getAtpApplicationIcon(application: IAtpDashboard): string {
    return application.submissionType === 'link' || application.applicationType === 'link'
      ? 'link'
      : 'assignment';
  }

  openFirmManagement(): void {
    this.seedFirmManagementFilters();
    this.router.navigate(['/firms/management']);
  }

  openFirmApplications(): void {
    this.seedFirmApplicationFilters(this.defaultFirmApplicationStatuses);
    this.router.navigate(['/firms/applications']);
  }

  openSubmittedAndInReviewFirmApplications(event: Event): void {
    event.stopPropagation();
    this.seedFirmApplicationFilters([APPLICATION_STATUS.submitted, APPLICATION_STATUS.inReview]);
    this.router.navigate(['/firms/applications']);
  }

  openActionNeededFirmApplications(event: Event): void {
    event.stopPropagation();
    this.seedFirmApplicationFilters([APPLICATION_STATUS.actionNeeded]);
    this.router.navigate(['/firms/applications']);
  }

  openExpiringFirmCertificates(event: Event): void {
    event.stopPropagation();
    this.seedFirmManagementFilters([FIRM_CERTIFICATE_ATTENTION_FILTERS.expiring]);
    this.router.navigate(['/firms/management']);
  }

  openExpiredFirmCertificates(event: Event): void {
    event.stopPropagation();
    this.seedFirmManagementFilters([FIRM_CERTIFICATE_ATTENTION_FILTERS.expired]);
    this.router.navigate(['/firms/management']);
  }

  openCertificatesRequiringAttention(): void {
    this.seedFirmManagementFilters([
      FIRM_CERTIFICATE_ATTENTION_FILTERS.expiring,
      FIRM_CERTIFICATE_ATTENTION_FILTERS.expired,
    ]);
    this.router.navigate(['/firms/management']);
  }

  openNewFirmApplication(): void {
    this.openFirmApplicationDialog({ workflow: 'new' });
  }

  openLinkFirmApplication(): void {
    this.openFirmApplicationDialog({ workflow: 'link' });
  }

  openFirmEdit(firm: IFirm): void {
    const dialogRef = this.dialog.open(FirmEditComponent, {
      width: '95%',
      maxWidth: '1400px',
      height: this.isMobile ? '95%' : '94vh',
      autoFocus: false,
      data: { firmId: firm.id },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.refreshFirmDashboard();
      }
    });
  }

  openFirmApplication(application: IFirmApplication): void {
    this.openFirmApplicationDialog({ applicationId: application.id });
  }

  get showFullIndividualDashboard(): boolean {
    return (this.individualDashboard?.summary?.affiliatedIndividualCount || 0) > 0
      || (this.individualDashboard?.summary?.openApplicationCount || 0) > 0;
  }

  get individualSummaryCardOrder(): DashboardSummaryCardKey[] {
    const attentionCount = (this.individualDashboard?.summary?.expiringCertificateCount || 0)
      + (this.individualDashboard?.summary?.expiredCertificateCount || 0);

    return this.getSummaryCardOrder(
      attentionCount > 0,
      (this.individualDashboard?.summary?.actionNeededApplicationCount || 0) > 0,
    );
  }

  openComplaintDialog(): void {
    this.complaintDialogService.open();
  }

  openIndividualManagement(): void {
    this.seedIndividualManagementFilters();
    this.router.navigate(['/individuals/management']);
  }

  openIndividualApplications(): void {
    this.seedIndividualApplicationFilters(this.defaultFirmApplicationStatuses);
    this.router.navigate(['/individuals/applications']);
  }

  openSubmittedAndInReviewIndividualApplications(event: Event): void {
    event.stopPropagation();
    this.seedIndividualApplicationFilters([APPLICATION_STATUS.submitted, APPLICATION_STATUS.inReview]);
    this.router.navigate(['/individuals/applications']);
  }

  openActionNeededIndividualApplications(event: Event): void {
    event.stopPropagation();
    this.seedIndividualApplicationFilters([APPLICATION_STATUS.actionNeeded]);
    this.router.navigate(['/individuals/applications']);
  }

  openExpiringIndividualCertificates(event: Event): void {
    event.stopPropagation();
    this.seedIndividualManagementFilters('Both', [], new Date(), this.addMonthsToDate(new Date(), 6));
    this.router.navigate(['/individuals/management']);
  }

  openExpiredIndividualCertificates(event: Event): void {
    event.stopPropagation();
    this.seedIndividualManagementFilters('Both', [], null, this.addDaysToDate(new Date(), -1));
    this.router.navigate(['/individuals/management']);
  }

  openIndividualCertificatesRequiringAttention(): void {
    this.seedIndividualManagementFilters('Both', [], null, this.addMonthsToDate(new Date(), 6));
    this.router.navigate(['/individuals/management']);
  }

  openNewIndividualApplication(): void {
    this.openIndividualApplicationDialog({ workflow: 'new' });
  }

  openLinkIndividualApplication(): void {
    this.openIndividualApplicationDialog({ workflow: 'link' });
  }

  openIndividualEdit(individual: IIndividual): void {
    const dialogRef = this.dialog.open(IndividualEditComponent, {
      width: '95%',
      maxWidth: '1400px',
      height: this.isMobile ? '95%' : '94vh',
      autoFocus: false,
      data: { personId: individual.id },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.refreshIndividualDashboard();
      }
    });
  }

  openIndividualApplication(application: IIndividualApplication): void {
    this.openIndividualApplicationDialog({ applicationId: application.id });
  }

  getIndividualStatusLabel(individual: IIndividual): string {
    return individual.inactive ? 'Inactive' : 'Active';
  }

  getIndividualVisibilityLabel(individual: IIndividual): string {
    return individual.response === 'Yes' ? 'Public' : 'Private';
  }

  getIndividualPrimaryExpiration(individual: IIndividual): string | null {
    const certificates = (individual.certificates || [])
      .filter((certificate) => !!certificate.expirationDate)
      .sort((left, right) => new Date(left.expirationDate || '').getTime() - new Date(right.expirationDate || '').getTime());
    return certificates[0]?.expirationDate || null;
  }

  getIndividualCertificateSummary(individual: IIndividual): string {
    return this.individualService.getCertificateSummary(individual);
  }

  getIndividualApplicationTypeLabel(application: IIndividualApplication): string {
    if (application.certificateType) {
      return application.certificateType;
    }

    return application.person?.id ? 'Account Link' : 'Unknown';
  }

  getIndividualApplicationActionLabel(application: IIndividualApplication): string {
    return application.status === APPLICATION_STATUS.inProgress || application.status === APPLICATION_STATUS.actionNeeded
      ? 'Click to continue application'
      : 'Click to view details';
  }

  getIndividualApplicationIcon(application: IIndividualApplication): string {
    return application.certificateType ? 'assignment' : 'link';
  }

  getFirmApplicationTypeLabel(applicationType: string): string {
    const value = (applicationType || '').toLowerCase();
    if (value === FIRM_APPLICATION_TYPES.rrp) {
      return 'RRP';
    }
    if (value === FIRM_APPLICATION_TYPES.lbpa) {
      return 'LBPA';
    }
    if (value === FIRM_APPLICATION_TYPES.link) {
      return 'Account Link';
    }
    return applicationType || 'Unknown';
  }

  getFirmApplicationActionLabel(application: IFirmApplication): string {
    return application.applicationStatus === APPLICATION_STATUS.inProgress || application.applicationStatus === APPLICATION_STATUS.actionNeeded
      ? 'Click to continue application'
      : 'Click to view details';
  }

  getFirmApplicationIcon(application: IFirmApplication): string {
    if (application.applicationType === FIRM_APPLICATION_TYPES.link) {
      return 'link';
    }

    return 'assignment';
  }

  getFirmVisibilityLabel(firm: IFirm): string {
    return firm.isPrivate ? 'Private' : 'Public';
  }

  getFirmStatusLabel(firm: IFirm): string {
    return firm.inactive ? 'Inactive' : 'Active';
  }

  getFirmPrimaryExpiration(firm: IFirm): string | null {
    return firm.nextCertificateExpirationDate || firm.ccbExpirationDate || null;
  }

  getFirmCertificateSummary(firm: IFirm): string {
    if (!firm.certificateSummary) {
      return '0 active / 0 expiring / 0 expired';
    }

    return firm.certificateSummary.active + ' active / ' + firm.certificateSummary.expiring + ' expiring / ' + firm.certificateSummary.expired + ' expired';
  }

  private createEmptyAtpDashboardSummary(): AtpDashboardSummary {
    return {
      affiliatedAtpCount: 0,
      expiringAccreditationCount: 0,
      expiredAccreditationCount: 0,
      openApplicationCount: 0,
      submittedApplicationCount: 0,
      actionNeededApplicationCount: 0,
    };
  }

  private resetAtpDashboardState(): void {
    this.atpList = [];
    this.atpDashboardProviders = [];
    this.atpDashboardApplications = [];
    this.atpDashboardSummary = this.createEmptyAtpDashboardSummary();
  }

  private buildAtpDashboardState(): void {
    this.atpDashboardProviders = this.atpList.filter(item => item.tileType === 'atp');
    this.atpDashboardApplications = this.atpList.filter(item => item.tileType === 'application');

    this.atpDashboardSummary = {
      affiliatedAtpCount: this.atpDashboardProviders.length,
      expiringAccreditationCount: this.atpDashboardProviders.reduce((total, atp) => total + (atp.atpRenewalCount || 0), 0),
      expiredAccreditationCount: this.atpDashboardProviders.reduce((total, atp) => total + (atp.atpExpiredCount || 0), 0),
      openApplicationCount: this.atpDashboardApplications.length,
      submittedApplicationCount: this.atpDashboardApplications.filter(application =>
        application.applicationStatus === APPLICATION_STATUS.submitted || application.applicationStatus === APPLICATION_STATUS.inReview
      ).length,
      actionNeededApplicationCount: this.atpDashboardApplications.filter(application =>
        application.applicationStatus === APPLICATION_STATUS.actionNeeded
      ).length,
    };
  }

  private getSummaryCardOrder(hasAttention: boolean, hasActionNeeded: boolean): DashboardSummaryCardKey[] {
    const orderedCards: DashboardSummaryCardKey[] = [];

    if (hasAttention) {
      orderedCards.push('attention');
    }

    if (hasActionNeeded) {
      orderedCards.push('applications');
    }

    this.defaultSummaryCardOrder.forEach((card) => {
      if (!orderedCards.includes(card)) {
        orderedCards.push(card);
      }
    });

    return orderedCards;
  }

  private applyDefaultEntityPanelState(): void {
    this.atpPanelExpanded = this.hasDashboardEntityContent('atp');
    this.firmPanelExpanded = this.hasDashboardEntityContent('firm');
    this.individualPanelExpanded = this.hasDashboardEntityContent('individual');
  }

  private hasDashboardEntityContent(entity: DashboardEntityKey): boolean {
    if (entity === 'atp') {
      return this.showFullAtpDashboard;
    }

    if (entity === 'firm') {
      return this.featureFlags.enableFirms && this.showFullFirmDashboard;
    }

    if (entity === 'individual') {
      return this.featureFlags.enableIndividuals && this.showFullIndividualDashboard;
    }

    if (entity === 'complaint') {
      return true;
    }

    return false;
  }

  private seedAtpManagementFilters(status: string | null = null, expirationStartDate: Date | null = null, expirationEndDate: Date | null = null): void {
    this.commonService.atpManagementFilter.filterString = '';
    this.commonService.atpManagementFilter.filteredActive.setValue(status);
    this.commonService.atpManagementFilter.filterDiscipline.setValue(null);

    if (expirationStartDate) {
      this.commonService.atpManagementFilter.expirationStartDate.setValue(expirationStartDate);
    } else {
      this.commonService.atpManagementFilter.expirationStartDate.reset();
    }

    if (expirationEndDate) {
      this.commonService.atpManagementFilter.expirationEndDate.setValue(expirationEndDate);
    } else {
      this.commonService.atpManagementFilter.expirationEndDate.reset();
    }
  }

  private seedAtpApplicationFilters(statuses: string[]): void {
    this.commonService.atpApplicationFilter.filterString = '';
    this.commonService.atpApplicationFilter.selectedStatus.setValue(statuses);
    this.commonService.atpApplicationFilter.selectedType.setValue([]);
    this.commonService.atpApplicationFilter.applicationDateFilterStart = null;
    this.commonService.atpApplicationFilter.applicationDateFilterEnd = null;
    this.commonService.atpApplicationFilter.selectedRenewal.setValue('');
  }

  private seedFirmManagementFilters(certificateStatuses: string[] = []): void {
    this.commonService.firmManagementFilter.filterString = '';
    this.commonService.firmManagementFilter.filteredActive.setValue('Both');
    this.commonService.firmManagementFilter.expirationStartDate.reset();
    this.commonService.firmManagementFilter.expirationEndDate.reset();
    this.commonService.firmManagementFilter.selectedCertificateStatuses.setValue(certificateStatuses);
  }

  private seedFirmApplicationFilters(statuses: string[]): void {
    this.commonService.firmApplicationFilter.filterString = '';
    this.commonService.firmApplicationFilter.selectedType.setValue([]);
    this.commonService.firmApplicationFilter.applicationDateFilterStart = null;
    this.commonService.firmApplicationFilter.applicationDateFilterEnd = null;
    this.commonService.firmApplicationFilter.selectedStatus.setValue(statuses);
  }

  private seedIndividualManagementFilters(status: string = 'Both', disciplineTypes: string[] = [], expirationStartDate: Date | null = null, expirationEndDate: Date | null = null): void {
    this.commonService.individualManagementFilter.filterString = '';
    this.commonService.individualManagementFilter.filteredActive.setValue(status);
    this.commonService.individualManagementFilter.disciplineTypes.setValue(disciplineTypes);

    if (expirationStartDate) {
      this.commonService.individualManagementFilter.expirationStartDate.setValue(expirationStartDate);
    } else {
      this.commonService.individualManagementFilter.expirationStartDate.reset();
    }

    if (expirationEndDate) {
      this.commonService.individualManagementFilter.expirationEndDate.setValue(expirationEndDate);
    } else {
      this.commonService.individualManagementFilter.expirationEndDate.reset();
    }
  }

  private seedIndividualApplicationFilters(statuses: string[]): void {
    this.commonService.individualApplicationFilter.filterString = '';
    this.commonService.individualApplicationFilter.selectedType.setValue([]);
    this.commonService.individualApplicationFilter.applicationDateFilterStart = null;
    this.commonService.individualApplicationFilter.applicationDateFilterEnd = null;
    this.commonService.individualApplicationFilter.selectedStatus.setValue(statuses);
  }

  private addMonthsToDate(date: Date, months: number): Date {
    const nextDate = new Date(date);
    nextDate.setMonth(nextDate.getMonth() + months);
    return nextDate;
  }

  private addDaysToDate(date: Date, days: number): Date {
    const nextDate = new Date(date);
    nextDate.setDate(nextDate.getDate() + days);
    return nextDate;
  }

  private openAtpApplicationDialog(data: { atpApplicationId?: number; initialSubmissionType?: 'new' | 'link' }): void {
    const dialogRef = this.dialog.open(AtpApplicationComponent, this.sharedService.getWizardDialogConfig({
      data,
    }));

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.saved) {
        this.refreshAtpDashboard();
      }
    });
  }

  private openFirmApplicationDialog(data: { applicationId?: number; workflow?: 'new' | 'link' }): void {
    const dialogRef = this.dialog.open(FirmApplicationComponent, {
      width: '95%',
      autoFocus: false,
      data,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.refreshFirmDashboard();
      }
    });
  }

  private openIndividualApplicationDialog(data: { applicationId?: number; workflow?: 'new' | 'link' }): void {
    const dialogRef = this.dialog.open(IndividualApplicationComponent, {
      width: '95%',
      autoFocus: false,
      data,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.saved || result) {
        this.refreshIndividualDashboard();
      }
    });
  }
}
